import { NextRequest, NextResponse } from 'next/server';
import { LOCALES, DEFAULT_LOCALE, matchLocale } from '@ai-radar/shared';

/**
 * Redirects unprefixed paths to a locale.
 *
 * Named `proxy` rather than `middleware`: Next.js 16 renamed the file and
 * the export, and the edge runtime is not available here.
 *
 * Only ever fires on a path with no locale prefix. Every real URL carries
 * one, so crawlers and shared links never hit this — which matters,
 * because content-negotiated redirects confuse indexing.
 */
export function proxy(request: NextRequest) {
  const { pathname } = request.nextUrl;

  // Already prefixed: nothing to do.
  const hasLocale = LOCALES.some(
    (locale) => pathname === `/${locale}` || pathname.startsWith(`/${locale}/`),
  );
  if (hasLocale) return NextResponse.next();

  // A returning visitor's choice beats their browser setting.
  const saved = request.cookies.get('locale')?.value;
  const preferred =
    saved && LOCALES.includes(saved as (typeof LOCALES)[number])
      ? (saved as (typeof LOCALES)[number])
      : matchLocale(request.headers.get('accept-language'));

  const url = request.nextUrl.clone();
  url.pathname = `/${preferred}${pathname === '/' ? '' : pathname}`;

  // 307, not 308: the choice depends on the request, so it must not be
  // cached as a permanent mapping.
  return NextResponse.redirect(url, 307);
}

export const config = {
  matcher: [
    /*
     * Skip Next internals and anything that looks like a file, plus:
     *  - robots.txt and sitemap.xml, which are locale-independent by
     *    definition and must stay at the root
     *  - /admin, an English-only operator tool that would otherwise
     *    redirect to /en/admin, where no route exists
     *  - /confirm, /unsubscribe, /preferences. These URLs are printed
     *    in emails that have already been sent, so they must keep
     *    working exactly as written — a redirect would break every
     *    link in the archive, and unsubscribe in particular has to
     *    work first time or the reader reports us as spam instead.
     */
    '/((?!_next|api|admin|confirm|unsubscribe|preferences|favicon|icon|robots\\.txt|sitemap\\.xml|.*\\..*).*)',
  ],
};

export { DEFAULT_LOCALE };
